import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StoreContext } from '../../../models/store/context';
import { StoreEvent } from '../../../enums/storeEvent';
import { saveSession } from '../../../services/session/save';
import { log } from '@domgen/shared/src/utils/log';
import { RegOnlyResponse } from '../../../models/services/api/postQuote';

/**
 * Converts existing quote data into a registration-only confirmation payload.
 *
 * Navigates to confirmation and persists the derived reg-only data.
 *
 * @param context Current store context.
 * @param _event Unused event payload.
 * @param enqueue Store enqueue helpers used for navigation emission.
 * @returns Updated context with derived reg-only data.
 */
export const regOnly = <T extends EventObject = EventObject>(
  context: StoreContext,
  _event: T,
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.REG_ONLY });
  const quote = context.quote?.data;

  enqueue.emit.navigate({ path: AppRoute.CONFIRMATION });

  const updatedContext = {
    ...context,
    confirmation: {
      ...context.confirmation,
      regOnlyData: {
        personId: quote?.personId ?? '',
        items:
          quote?.items.map((item) => ({
            id: item.id,
            operationalStatusAtPos: item.operationalStatusAtPos,
            guarantee: item.guarantee,
            labour: item.labour,
          })) || [],
      } satisfies RegOnlyResponse,
    },
  };

  saveSession(updatedContext);
  return updatedContext;
};
